import { format } from 'date-fns';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { trpc } from '~/api/trpc';
import type { CreateCancellationFeePrefill } from '~/components/cancellation-fees/CreateCancellationFeeDialog';
import { cancellationFeeAppointmentStatusLabels, hasAppointmentEnded } from '~/lib/cancellation-fee-config';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

type TodaysCancelledAppointmentsTableProps = {
    onCreate: (prefill: CreateCancellationFeePrefill) => void;
};

const formatTime = (date: string | Date | null) => (date ? format(new Date(date), 'HH:mm') : '–');
const formatDateTime = (date: string | Date | null) => (date ? format(new Date(date), 'dd.MM.yyyy HH:mm') : '–');

export const TodaysCancelledAppointmentsTable = ({ onCreate }: TodaysCancelledAppointmentsTableProps) => {
    const navigate = useNavigate();
    const { role } = useUserStore(
        useShallow((state) => ({ role: state.user?.role }))
    );
    const isAdmin = role === UserRole.ADMIN;

    const [selectedStoreId, setSelectedStoreId] = useState<string>(isAdmin ? 'all' : '');

    const { data: storesData } = trpc.stores.getAll.useQuery({ filters: {} });
    const stores = useMemo(() => {
        const data = storesData?.data || [];
        return [...data].sort((a, b) => a.name.localeCompare(b.name));
    }, [storesData?.data]);

    useEffect(() => {
        if (isAdmin && selectedStoreId === '') {
            setSelectedStoreId('all');
        } else if (!isAdmin && stores.length > 0 && !selectedStoreId) {
            setSelectedStoreId(String(stores[0].id));
        }
    }, [stores, isAdmin, selectedStoreId]);

    const { data: appointments, isLoading } = trpc.cancellationFees.getTodaysAppointments.useQuery(
        { storeId: selectedStoreId !== 'all' && selectedStoreId !== '' ? parseInt(selectedStoreId) : undefined },
        { enabled: selectedStoreId !== '', refetchInterval: 60_000 }
    );

    return (
        <>
            <div className='flex justify-end mb-4'>
                <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
                    <SelectTrigger className='w-[200px]'>
                        <SelectValue placeholder='Alle Filialen' />
                    </SelectTrigger>
                    <SelectContent>
                        {isAdmin && <SelectItem value='all'>Alle Filialen</SelectItem>}
                        {stores.map((store) => (
                            <SelectItem key={store.id} value={String(store.id)}>
                                {store.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <Card>
                <CardContent className='p-0'>
                    {isLoading || selectedStoreId === '' ? (
                        <div className='p-6 space-y-4'>
                            {[...Array(3)].map((_, i) => (
                                <Skeleton key={i} className='h-12 w-full' />
                            ))}
                        </div>
                    ) : (
                        <Table className='text-sm'>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Kunde</TableHead>
                                    <TableHead>Termin</TableHead>
                                    <TableHead>Filiale</TableHead>
                                    <TableHead>Absage</TableHead>
                                    <TableHead>Ausfallentschädigung</TableHead>
                                    <TableHead />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {appointments && appointments.length > 0 ? (
                                    appointments.map((apt) => {
                                        const statusInfo = cancellationFeeAppointmentStatusLabels[apt.status]
                                            ?? { label: apt.status, variant: 'outline' as const };
                                        const feeDraft = apt.activeCancellationFeeDraft;
                                        const hasEnded = hasAppointmentEnded(apt.endDate);
                                        return (
                                            <TableRow key={apt.id}>
                                                <TableCell className='align-top'>
                                                    <div
                                                        className='font-medium cursor-pointer hover:underline text-primary'
                                                        onClick={() => navigate(`/customer/${apt.customer.id}`)}
                                                    >
                                                        {apt.customer.firstName} {apt.customer.lastName}
                                                    </div>
                                                    {apt.customer.email && (
                                                        <div className='text-muted-foreground break-all'>{apt.customer.email}</div>
                                                    )}
                                                </TableCell>
                                                <TableCell className='align-top'>
                                                    <div className='flex items-center gap-2 whitespace-nowrap'>
                                                        <span className='font-medium'>
                                                            {formatDateTime(apt.startDate)} – {formatTime(apt.endDate)} Uhr
                                                        </span>
                                                        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                                                    </div>
                                                    <div className='text-muted-foreground line-clamp-1' title={apt.title}>
                                                        {apt.title}
                                                    </div>
                                                </TableCell>
                                                <TableCell className='align-top'>{apt.store?.name ?? '–'}</TableCell>
                                                <TableCell className='align-top'>
                                                    {apt.cancellation ? (
                                                        <div>
                                                            <div className='font-medium whitespace-nowrap'>
                                                                {apt.cancellation.hoursBefore} Std. vorher
                                                                <span className='font-normal text-muted-foreground'>
                                                                    {' '}(am {formatTime(apt.cancellation.cancelledAt)} Uhr)
                                                                </span>
                                                            </div>
                                                            {apt.cancellation.reason && (
                                                                <div className='line-clamp-2' title={apt.cancellation.reason}>
                                                                    <span className='text-muted-foreground'>Grund:</span> {apt.cancellation.reason}
                                                                </div>
                                                            )}
                                                            {apt.cancellation.note && (
                                                                <div className='line-clamp-2 text-muted-foreground' title={apt.cancellation.note}>
                                                                    {apt.cancellation.note}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className='text-muted-foreground'>–</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className='align-top'>
                                                    {feeDraft ? (
                                                        <Badge
                                                            variant='default'
                                                            className='cursor-pointer whitespace-nowrap'
                                                            onClick={() => navigate(`/billing-draft/${feeDraft.id}`)}
                                                        >
                                                            {feeDraft.billingDraftNumber}
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant='outline'>Offen</Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell className='align-top text-right'>
                                                    {!feeDraft && (
                                                        <Button
                                                            size='sm'
                                                            disabled={!hasEnded}
                                                            title={hasEnded ? undefined : 'Eine Ausfallentschädigung kann erst nach Terminende erstellt werden'}
                                                            onClick={() => onCreate({
                                                                customer: {
                                                                    id: apt.customer.id,
                                                                    firstName: apt.customer.firstName,
                                                                    lastName: apt.customer.lastName,
                                                                    email: apt.customer.email ?? null,
                                                                },
                                                                appointmentId: apt.id,
                                                            })}
                                                        >
                                                            <Plus className='mr-1 h-4 w-4' />
                                                            Erstellen
                                                        </Button>
                                                    )}
                                                    {!feeDraft && !hasEnded && (
                                                        <div className='mt-1 text-xs text-muted-foreground whitespace-nowrap'>
                                                            Ab {formatTime(apt.endDate)} Uhr möglich
                                                        </div>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} className='h-24 text-center'>
                                            Heute keine kurzfristigen Absagen oder nicht wahrgenommenen Termine.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </>
    );
};
